import React, { useState, Suspense, lazy } from "react";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import AuspiciousToday from "@/components/AuspiciousToday";
import SceneBackground, { SceneAnchor } from "@/components/SceneBackground";
import AmbientBackground from "@/components/AmbientBackground";
import { useLocation } from "@/contexts/LocationContext";
import { IntentProvider } from "@/contexts/IntentContext";

// Below-the-fold sections are lazy-loaded so the initial JS bundle stays small.
// This is the second-biggest performance win after dropping three.js on mobile:
// it cuts the initial bundle by ~150KB and removes a lot of upfront parse cost.
const SubscribeCTA = lazy(() => import("@/components/SubscribeCTA"));
const WheelSection = lazy(() => import("@/components/WheelSection"));
const TopAuspicious = lazy(() => import("@/components/TopAuspicious"));
const Manifesto = lazy(() => import("@/components/Manifesto"));
const Timeline = lazy(() => import("@/components/Timeline"));
const About = lazy(() => import("@/components/About"));
const Marquee = lazy(() => import("@/components/Marquee"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Footer = lazy(() => import("@/components/Footer"));
const SlotModal = lazy(() => import("@/components/SlotModal"));

// Defer transitions purely visually — they're tiny SVG overlays.
const DotsTransition = lazy(() =>
  import("@/components/SectionTransition").then((m) => ({ default: m.DotsTransition }))
);
const ArcTransition = lazy(() =>
  import("@/components/SectionTransition").then((m) => ({ default: m.ArcTransition }))
);

// Suspense fallback is intentionally empty — sections fade in as they hydrate.
const Blank = () => <div aria-hidden style={{ minHeight: 0 }} />;

export default function Landing() {
  const [modalSlot, setModalSlot] = useState(null);
  const { location } = useLocation();

  return (
    <IntentProvider>
      <div className="relative grain min-h-screen">
        <AmbientBackground />
        <SceneBackground />
        <div className="relative z-[1]">
          <Nav />
          <main id="main" role="main">
            <SceneAnchor id="hero"><Hero onSlotClick={setModalSlot} /></SceneAnchor>
            <AuspiciousToday onSlotClick={setModalSlot} />
            <Suspense fallback={<Blank />}>
              {/* Wheel sits in what used to be the prologue slot — heavier
                  visual moment lands first so users orient before reading. */}
              <SceneAnchor id="wheel"><WheelSection onSlotClick={setModalSlot} /></SceneAnchor>
              <SceneAnchor id="manifesto"><Manifesto /></SceneAnchor>
              {/* TopAuspicious + Timeline (Sixteen Windows) back in. Calendar
                  also lives in the AuspiciousToday section above. */}
              <SceneAnchor id="timeline">
                <TopAuspicious onPick={setModalSlot} />
                <DotsTransition variant="dark" />
                <Timeline onSlotClick={setModalSlot} />
              </SceneAnchor>
              <ArcTransition variant="dark" />
              <SceneAnchor id="about"><About onSlotClick={setModalSlot} /></SceneAnchor>
              <Marquee />
              <SubscribeCTA />
              <SceneAnchor id="faq"><FAQ /></SceneAnchor>
              <SceneAnchor id="footer"><Footer /></SceneAnchor>
            </Suspense>
          </main>
        </div>
        <Suspense fallback={null}>
          <SlotModal slot={modalSlot} location={location} onClose={() => setModalSlot(null)} />
        </Suspense>
      </div>
    </IntentProvider>
  );
}
